/**
 * Reporting form composer shape + defaults (shared by Redux + localStorage restore).
 */

import {
  createInitialDescriptionBlocks,
  ReportDescriptionBlock,
} from './reportDescriptionBlocks';
import { ReportActivitySet } from './reportActivitySets';
import { ReportChartsMetricMode } from './reportMetrics';
import { ReportContextUser } from './reportContextUsers';

/** New reports: details first, then body. Replies start on body. */
export type ComplianceComposerPhase = 'details' | 'body';

export type PreparedReportDraft = {
  id: string,
  viewUrl?: string | null,
  preview?: string,
};

/** Composer form — kept in Redux + localStorage so refresh restores it (per staff, not shared). */
export type ComplianceComposerState = {
  toEmail: string,
  /** Mandatory one-line subject for the report. */
  subject: string,
  descBlocks: ReportDescriptionBlock[],
  activeTextBlockId: string,
  includeActivityLogs: boolean,
  /** Frozen ActivityLogs snapshots pinned for this report (serial 1..n). */
  activitySets: ReportActivitySet[],
  /**
   * @deprecated Prefer ReportActivitySet.showCharts — kept so old localStorage
   * can seed per-set toggles on restore.
   */
  showCharts: boolean,
  /** Counts vs balances chart family (rightbar). */
  chartsMetricMode: ReportChartsMetricMode,
  previewPage: number,
  draft: PreparedReportDraft | null,
  /** Inbox report being answered — locks Send to / Purpose. */
  replyToReportId: string | null,
  replyToFromName: string,
  /** New-report wizard step. Replies always use body. */
  composerPhase: ComplianceComposerPhase,
  /** Up to 20 people in context (staff or customers), each Good / Neutral / Incident / Alert. */
  contextUsers: ReportContextUser[],
  /** Required when any in-context person is Incident or Alert. */
  alertLevelSummary: string,
};

export const createDefaultComplianceComposer = (): ComplianceComposerState => ({
  toEmail: '',
  subject: '',
  descBlocks: createInitialDescriptionBlocks(),
  activeTextBlockId: '',
  includeActivityLogs: false,
  activitySets: [],
  showCharts: true,
  chartsMetricMode: 'balances',
  previewPage: 1,
  draft: null,
  replyToReportId: null,
  replyToFromName: '',
  composerPhase: 'details',
  contextUsers: [],
  alertLevelSummary: '',
});
